import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'en' | 'ko';

interface LanguageState {
    language: Language;
    setLanguage: (language: Language) => void;
}

export const useLanguage = create<LanguageState>()(
    persist(
        (set) => ({
            language: 'ko',
            setLanguage: (language: Language) => set({ language }),
        }),
        {
            name: 'language-storage',
        }
    )
);

// 번역 텍스트 타입
export interface TranslationKeys {
    // Navigation
    'nav.home': string;
    'nav.instagram': string;
    'nav.analytics': string;
    'nav.dashboard': string;
    'nav.pricing': string;
    'nav.auth': string;

    // Hero Section
    'hero.title': string;
    'hero.subtitle': string;
    'hero.cta': string;
    'hero.features.instant': string;
    'hero.features.results': string;
    'hero.features.guarantee': string;

    // Features
    'features.title': string;
    'features.subtitle': string;
    'features.realtime.title': string;
    'features.realtime.desc': string;
    'features.analysis.title': string;
    'features.analysis.desc': string;
    'features.tracking.title': string;
    'features.tracking.desc': string;
    'features.ai.title': string;
    'features.ai.desc': string;
    'features.interest.title': string;
    'features.interest.desc': string;
    'features.anonymity.title': string;
    'features.anonymity.desc': string;

    // Use Cases
    'usecases.title': string;
    'usecases.ex.title': string;
    'usecases.ex.desc': string;
    'usecases.crush.title': string;
    'usecases.crush.desc': string;
    'usecases.colleagues.title': string;
    'usecases.colleagues.desc': string;
    'usecases.suspicious.title': string;
    'usecases.suspicious.desc': string;
    'usecases.family.title': string;
    'usecases.family.desc': string;
    'usecases.special.title': string;
    'usecases.special.desc': string;

    // Testimonials
    'testimonials.title': string;
    'testimonials.user1.text': string;
    'testimonials.user2.text': string;
    'testimonials.user3.text': string;

    // Common
    'common.loading': string;
    'common.error': string;
    'common.success': string;
    'common.active': string;
    'common.score': string;
    'common.allTime': string;
    'common.visits': string;
    'common.interest': string;
    'common.pattern': string;
    'common.location': string;
    'common.device': string;
    'common.lastVisit': string;
    'common.stayTime': string;
    'common.complete': string;
    'common.analyzing': string;

    // CTA
    'cta.discover': string;
    'cta.start': string;
    'cta.learn': string;
    'cta.viewPricing': string;
}

// 번역 데이터
export const translations: Record<Language, TranslationKeys> = {
    en: {
        // Navigation
        'nav.home': 'Home',
        'nav.instagram': 'Instagram',
        'nav.analytics': 'Analytics',
        'nav.dashboard': 'Dashboard',
        'nav.pricing': 'Pricing',
        'nav.auth': 'Sign In',

        // Hero Section
        'hero.title': "Who's secretly watching your Instagram?",
        'hero.subtitle': 'Discover exactly when, how many times, and how long specific people viewed your Instagram profile. Get detailed analysis results within 24 hours.',
        'hero.cta': 'Discover Who\'s Watching',
        'hero.features.instant': 'Instant analysis start',
        'hero.features.results': 'Results within 24h',
        'hero.features.guarantee': 'Money-back guarantee',

        // Features
        'features.title': 'Everything you need to know',
        'features.subtitle': 'Comprehensive Instagram tracking and analysis system',
        'features.realtime.title': 'Real-time Visit Tracking',
        'features.realtime.desc': 'See who is watching your Instagram profile right now in real-time.',
        'features.analysis.title': '24-Hour Complete Analysis',
        'features.analysis.desc': 'Get detailed analysis of when, how many times, and how long they viewed your profile.',
        'features.tracking.title': 'Track Up to 5 People',
        'features.tracking.desc': 'Monitor visit patterns of specific people you care about simultaneously.',
        'features.ai.title': 'AI Pattern Analysis',
        'features.ai.desc': 'AI analyzes visit patterns to predict when they view your profile most.',
        'features.interest.title': 'Interest Score',
        'features.interest.desc': 'Measure how interested each person is in you on a 1-100 scale.',
        'features.anonymity.title': 'Complete Anonymity',
        'features.anonymity.desc': 'Your tracking activities remain completely confidential.',

        // Use Cases
        'usecases.title': 'Here\'s exactly what you\'ll discover',
        'usecases.ex.title': 'Ex-Partner Tracking',
        'usecases.ex.desc': '"John checks your account every day at 7 PM (total 12 times)"',
        'usecases.crush.title': 'Crush Monitoring',
        'usecases.crush.desc': '"Sarah views your story within 15 minutes of posting on average"',
        'usecases.colleagues.title': 'Workplace Colleagues',
        'usecases.colleagues.desc': '"3 colleagues viewed your vacation photos for 2 hours"',
        'usecases.suspicious.title': 'Suspicious Accounts',
        'usecases.suspicious.desc': '"fake_account123 checks you daily at 2 AM"',
        'usecases.family.title': 'Family & Friends',
        'usecases.family.desc': '"Mom checks your updates every morning at 8 AM"',
        'usecases.special.title': 'That Special Someone',
        'usecases.special.desc': '"Alex views you 3 times a week, 5 minutes each time"',

        // Testimonials
        'testimonials.title': 'Over 1,247 people already discovered the truth',
        'testimonials.user1.text': 'Found out my ex was checking my account 3 times daily. The accuracy is incredible!',
        'testimonials.user2.text': 'Confirmed my crush actually views all my stories. Gave me the confidence to ask her out.',
        'testimonials.user3.text': 'Getting this detailed analysis is absolutely amazing value!',

        // Common
        'common.loading': 'Loading...',
        'common.error': 'Error',
        'common.success': 'Success',
        'common.active': 'Active',
        'common.score': 'Score',
        'common.allTime': 'All time',
        'common.visits': 'visits',
        'common.interest': 'Interest',
        'common.pattern': 'Visit pattern',
        'common.location': 'Location',
        'common.device': 'Device',
        'common.lastVisit': 'Last visit',
        'common.stayTime': 'Avg stay time',
        'common.complete': 'Complete',
        'common.analyzing': 'Analyzing',

        // CTA
        'cta.discover': 'Discover Who\'s Watching',
        'cta.start': 'Start Analysis',
        'cta.learn': 'Learn More',
        'cta.viewPricing': 'View Pricing',
    },
    ko: {
        // Navigation
        'nav.home': '홈',
        'nav.instagram': '인스타그램',
        'nav.analytics': '분석',
        'nav.dashboard': '대시보드',
        'nav.pricing': '요금제',
        'nav.auth': '로그인',

        // Hero Section
        'hero.title': '누가 당신의 인스타그램을 몰래 보고 있을까요?',
        'hero.subtitle': '특정 사람이 당신의 인스타그램 프로필을 언제, 몇 번, 얼마나 오래 봤는지 정확히 알아보세요. 24시간 내에 상세한 분석 결과를 받을 수 있습니다.',
        'hero.cta': '누가 보고 있는지 확인하기',
        'hero.features.instant': '즉시 분석 시작',
        'hero.features.results': '24시간 내 결과',
        'hero.features.guarantee': '환불 보장',

        // Features
        'features.title': '알아야 할 모든 것',
        'features.subtitle': '포괄적인 인스타그램 추적 및 분석 시스템',
        'features.realtime.title': '실시간 방문 추적',
        'features.realtime.desc': '누가 당신의 인스타그램 프로필을 지금 보고 있는지 실시간으로 확인하세요.',
        'features.analysis.title': '24시간 완전 분석',
        'features.analysis.desc': '언제, 몇 번, 얼마나 오래 봤는지 시간 단위로 정확한 분석을 제공합니다.',
        'features.tracking.title': '최대 5명 동시 추적',
        'features.tracking.desc': '관심있는 특정 사람들의 방문 패턴을 동시에 모니터링하세요.',
        'features.ai.title': 'AI 패턴 분석',
        'features.ai.desc': '인공지능이 방문 패턴을 분석해 언제 가장 많이 보는지 예측합니다.',
        'features.interest.title': '관심도 점수',
        'features.interest.desc': '1-100점 척도로 각 사람이 당신에게 얼마나 관심이 있는지 측정합니다.',
        'features.anonymity.title': '완전한 익명성',
        'features.anonymity.desc': '당신의 분석 활동은 완전히 비밀이 보장됩니다.',

        // Use Cases
        'usecases.title': '실제로 이런 것들을 정확히 알 수 있어요',
        'usecases.ex.title': '전 연인 추적',
        'usecases.ex.desc': '"김OO가 매일 오후 7시경에 당신 계정을 확인해요 (총 12번)"',
        'usecases.crush.title': '짝사랑 상대',
        'usecases.crush.desc': '"박OO가 당신 스토리를 올린 후 평균 15분 내에 확인해요"',
        'usecases.colleagues.title': '직장 동료',
        'usecases.colleagues.desc': '"회사 동료 3명이 당신의 휴가 사진을 2시간 동안 봤어요"',
        'usecases.suspicious.title': '의심스러운 계정',
        'usecases.suspicious.desc': '"fake_account123이 새벽 2시에 당신을 매일 확인해요"',
        'usecases.family.title': '가족/친구',
        'usecases.family.desc': '"어머니가 매일 오전 8시에 당신 근황을 확인하세요"',
        'usecases.special.title': '궁금한 그 사람',
        'usecases.special.desc': '"이OO가 당신을 주 3회, 매번 5분씩 자세히 봐요"',

        // Testimonials
        'testimonials.title': '이미 1,247명이 진실을 확인했어요',
        'testimonials.user1.text': '전 남친이 매일 내 계정을 3번씩 보고 있다는 걸 알게 됐어요. 정말 정확해요!',
        'testimonials.user2.text': '짝사랑하는 사람이 실제로 내 스토리를 매번 본다는 걸 확인했어요. 용기 낼 수 있었어요.',
        'testimonials.user3.text': '이렇게 상세한 분석을 받을 수 있다니... 완전 대박이에요!',

        // Common
        'common.loading': '로딩 중...',
        'common.error': '오류',
        'common.success': '성공',
        'common.active': '활성',
        'common.score': '점수',
        'common.allTime': '전체 기간',
        'common.visits': '방문',
        'common.interest': '관심도',
        'common.pattern': '방문 패턴',
        'common.location': '위치',
        'common.device': '기기',
        'common.lastVisit': '마지막 방문',
        'common.stayTime': '평균 체류시간',
        'common.complete': '완료',
        'common.analyzing': '분석 중',

        // CTA
        'cta.discover': '누가 보고 있는지 확인하기',
        'cta.start': '분석 시작하기',
        'cta.learn': '자세히 알아보기',
        'cta.viewPricing': '요금제 보기',
    },
};

// 번역 함수
export const useTranslation = () => {
    const { language } = useLanguage();

    const t = (key: keyof TranslationKeys): string => {
        return translations[language][key] || key;
    };

    return { t, language };
}; 